/**
 * Footer: /video-renewal 전용 하단 툴바 컨테이너.
 *
 * v1 원본: RenewalComponents/Headers/Footer/FooterContainer.js + FooterPresenter.js
 * v1 CSS: .rtc_new3 .header { position: absolute; bottom: 0; left: 0; right: 0; }
 *         .rtc_header > .newHeader_btnGroup
 *
 * ⚠ v1에서는 이 요소가 id="header" class="header"를 사용하며 하단에 배치됨.
 * ⚠ "더보기" 클릭 시 MoreMenu 드롭다운이 아닌 SettingsDialog(설정 다이얼로그)가 열린다.
 */
import React, { useState } from 'react';
import { useAppSelector } from '@/store/hooks';
import { isMasterUser } from '@/store/slices/masterSlice';
import apiManager from '@/api';

/** 개발기 빌드 여부 — 개발기(productDev/wehagoTest/로컬 dev)에서만 회의설정(녹화) 버튼을 무조건 노출한다 */
const DEV_APP_TYPES = new Set(['productDev', 'wehagoTest', 'dev', undefined]);
const IS_DEV_BUILD = DEV_APP_TYPES.has(import.meta.env.VITE_APP_TYPE as string | undefined);
import MicButton from './buttons/MicButton';
import OneAiButton from './buttons/OneAiButton';
import VideoButton from './buttons/VideoButton';
import SpeakerButton from './buttons/SpeakerButton';
import MemberButton from './buttons/MemberButton';
import ChatButton from './buttons/ChatButton';
import ReactionButton from './buttons/ReactionButton';
import DesktopShareButton from './buttons/DesktopShareButton';
import ChatHistoryButton from './buttons/ChatHistoryButton';
import TranslationButton from './buttons/TranslationButton';
import LeaveMenu from './menus/LeaveMenu';
import MeetingSettingsButton from './buttons/MeetingSettingsButton';
import SettingsDialog from './SettingsDialog';
import MeetingMaterialDialog from './MeetingMaterialDialog';

interface FooterProps {
  onToggleAudio: () => void;
  onToggleVideo: () => void;
  onToggleScreenShare: () => void;
  onHangup: () => void;
  onOpenSettings: () => void;
  onRequestFloor?: () => void;
  onToggleRecording?: () => void;
  onStopSketch?: () => void;
  onStopDocumentShare?: () => void;
  onChangeDevice?: (type: string, deviceId: string) => void;
  onEndMeetingForAll?: () => void;
}

const Footer: React.FC<FooterProps> = ({
  onToggleAudio,
  onToggleVideo,
  onToggleScreenShare,
  onHangup,
  onOpenSettings,
  onRequestFloor,
  onToggleRecording,
  onStopSketch,
  onStopDocumentShare,
  onChangeDevice,
  onEndMeetingForAll,
}) => {
  const local = useAppSelector((s) => s.local);
  const masterUsers = useAppSelector((s) => s.master.masterUsers);
  const isLocalMaster = isMasterUser(masterUsers, local.wehagoId);

  // SettingsDialog 상태 (v1 FooterContainer의 isMoreSettingDialog 패턴)
  const [isSettingsDialogOpen, setIsSettingsDialogOpen] = useState(false);
  const [settingsInitialMenu, setSettingsInitialMenu] = useState(0);
  const [isMeetingMaterialDialogOpen, setIsMeetingMaterialDialogOpen] = useState(false);

  // 더보기 버튼 클릭 → SettingsDialog 열기 (v1 handleMoreBtn)
  const handleMoreBtn = () => {
    setSettingsInitialMenu(0);
    setIsSettingsDialogOpen(true);
  };

  // 가상 배경 설정 열기 (v1 onOpenSettingsBackground)
  const handleOpenSettingsBackground = () => {
    setSettingsInitialMenu(3);
    setIsSettingsDialogOpen(true);
  };

  const handleOpenSettingsVideo = () => {
    setSettingsInitialMenu(1);
    setIsSettingsDialogOpen(true);
  };

  const handleOpenMeetingMaterial = () => {
    setIsMeetingMaterialDialogOpen(true);
  };

  return (
    <>
      <div
        id="header"
        className="header"
        style={{
          top: 'unset',
          minWidth: 'auto',
          height: 'auto',
          borderBottom: 'none',
          zIndex: 102,
        }}
      >
        <div className="rtc_header">
          {/* 좌측: 디바이스 컨트롤 (스피커/마이크/카메라) */}
          <div className="newHeader_btnGroup">
            <SpeakerButton onChangeDevice={onChangeDevice} />
            <MicButton onToggleAudio={onToggleAudio} onChangeDevice={onChangeDevice} />
            <VideoButton
              onToggleVideo={onToggleVideo}
              onOpenSettings={onOpenSettings}
              onOpenSettingsBackground={handleOpenSettingsBackground}
              onOpenSettingsVideo={handleOpenSettingsVideo}
              onChangeDevice={onChangeDevice}
            />
          </div>

          {/* 중앙: 기능 버튼 */}
          <div className="newHeader_btnGroup">
            <MemberButton />
            <ChatButton />
            <ReactionButton />
            <DesktopShareButton onToggleScreenShare={onToggleScreenShare} onOpenMeetingMaterial={handleOpenMeetingMaterial} />
            <ChatHistoryButton />
            {/* 번역 버튼: 방 설정에서 AI 번역이 활성화된 경우에만 노출 */}
            {local.aiTranslationType != null && <TranslationButton />}

            <OneAiButton />

            {/* 더보기 — 클릭 시 SettingsDialog 열기 (v1 원본 동작) */}
            <div className="newHeader_btnItem">
              <button
                type="button"
                className="newHeader_btn newHeader_btn--dropdown"
                onClick={handleMoreBtn}
              >
                <span className="newHeader_btnIcon icoMore" />
                <p className="newHeader_btnText">더보기</p>
              </button>
            </div>
          </div>

          {/* 우측: 나가기 */}
          <div className="newHeader_btnGroup">
            {/* 회의설정 (마스터 전용) — 개발기 빌드는 모든 마스터에게, 운영은 정식 오픈 전까지 더존비즈온(cno 9) 마스터만 */}
            {isLocalMaster && (IS_DEV_BUILD || Number(apiManager.selectedCno) === 9) && (
              <MeetingSettingsButton onToggleRecording={onToggleRecording} />
            )}
            <LeaveMenu
              isMaster={isLocalMaster}
              onHangup={onHangup}
              onEndMeetingForAll={onEndMeetingForAll}
            />
          </div>
        </div>
      </div>

      {/* SettingsDialog (v1 FooterContainer 패턴) */}
      {isSettingsDialogOpen && (
        <SettingsDialog
          onClose={() => {
            setIsSettingsDialogOpen(false);
            setSettingsInitialMenu(0);
          }}
          initialMenu={settingsInitialMenu}
          onChangeDevice={onChangeDevice}
        />
      )}

      {/* 회의자료 팝업 (화면공유 > 문서공유 > 회의자료 경로) */}
      {isMeetingMaterialDialogOpen && (
        <MeetingMaterialDialog onClose={() => setIsMeetingMaterialDialogOpen(false)} />
      )}
    </>
  );
};

export default Footer;
